import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import dataPrivacyService from '../../../services/dataPrivacyService';

const EncryptionStatusDashboard = ({ preferences, onVerify }) => {
  const encryption = preferences?.encryption || {};
  const lastVerified = encryption?.lastVerified;
  const certificateValidUntil = encryption?.certificateValidUntil;
  const daysUntilExpiration = dataPrivacyService?.getDaysUntilExpiration(certificateValidUntil);

  const getStatusColor = (enabled) => {
    return enabled ? 'text-green-600' : 'text-red-600';
  };

  const getStatusIcon = (enabled) => {
    return enabled ? 'CheckCircle2' : 'XCircle';
  };

  const getCertificateStatus = () => {
    if (!daysUntilExpiration) return { color: 'text-gray-500', text: 'Not verified' };
    if (daysUntilExpiration < 30) return { color: 'text-red-600', text: `Expires in ${daysUntilExpiration} days` };
    if (daysUntilExpiration < 90) return { color: 'text-yellow-600', text: `Expires in ${daysUntilExpiration} days` };
    return { color: 'text-green-600', text: `Valid for ${daysUntilExpiration} days` };
  };

  const certificateStatus = getCertificateStatus();

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-start gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Icon name="Shield" size={20} className="text-primary" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-foreground">Encryption Status Dashboard</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Real-time verification of data encryption protocols and certificate validity
          </p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Encryption Status Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* At-Rest Encryption */}
          <div className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <Icon 
                name={getStatusIcon(encryption?.atRestEnabled)} 
                size={20} 
                className={getStatusColor(encryption?.atRestEnabled)} 
              />
              <h3 className="font-medium text-foreground">At-Rest Encryption</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              {encryption?.atRestEnabled 
                ? 'All stored data is encrypted using AES-256' :'At-rest encryption is disabled'}
            </p>
          </div>

          {/* In-Transit Encryption */}
          <div className="bg-muted/30 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <Icon 
                name={getStatusIcon(encryption?.inTransitEnabled)} 
                size={20} 
                className={getStatusColor(encryption?.inTransitEnabled)} 
              />
              <h3 className="font-medium text-foreground">In-Transit Encryption</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              {encryption?.inTransitEnabled 
                ? 'All data transfers use TLS 1.3 encryption' :'In-transit encryption is disabled'}
            </p>
          </div>
        </div>

        {/* Certificate Validity */}
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="FileKey" size={18} className="text-foreground" />
                <h3 className="font-medium text-foreground">SSL/TLS Certificate</h3>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">
                  Last Verified: {dataPrivacyService?.formatDate(lastVerified)}
                </p>
                <p className={`text-sm font-medium ${certificateStatus?.color}`}>
                  {certificateStatus?.text}
                </p>
              </div>
            </div>
            <Button
              onClick={onVerify}
              variant="outline"
              size="sm"
              iconName="RefreshCw"
              iconPosition="left"
            >
              Verify Now
            </Button>
          </div>
        </div>

        {/* Compliance Badges */}
        <div className="flex flex-wrap gap-2">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-100 text-green-800 rounded-full text-xs font-medium">
            <Icon name="CheckCircle2" size={14} />
            SOC 2 Type II Certified
          </div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
            <Icon name="CheckCircle2" size={14} />
            GDPR Compliant
          </div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
            <Icon name="CheckCircle2" size={14} />
            HIPAA Ready
          </div>
        </div>

        {/* Security Notice */}
        <div className="bg-muted/50 rounded-lg p-4 flex items-start gap-3">
          <Icon name="Info" size={16} className="text-muted-foreground flex-shrink-0 mt-0.5" />
          <div className="text-xs text-muted-foreground">
            <p className="font-medium mb-1">Encryption Standards</p>
            <p>
              DraftIQ uses industry-standard AES-256 encryption for data at rest and TLS 1.3 for data in transit. 
              All encryption keys are managed through AWS KMS with automatic rotation every 90 days.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EncryptionStatusDashboard;
